import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowDown,
  FiArrowUpRight,
  FiHelpCircle,
  FiMessageCircle,
  FiSearch,
} from "react-icons/fi";
import Accordion from "../../components/Accordion";
import Footer from "../Footer/Footer";

const faqItems = [
  {
    question: "What kind of software development services do you offer?",
    answer:
      "We offer custom software development, web application development, desktop application development, mobile app development, and cloud application development services.",
  },
  {
    question: "What types of websites can you build?",
    answer:
      "We can build all types of websites, including static websites, dynamic websites, e-commerce websites, CMS-based websites, and web portals.",
  },
  {
    question: "Can you develop mobile apps for both iOS and Android platforms?",
    answer:
      "Yes, we have expertise in developing mobile apps for both iOS and Android platforms using native and hybrid app development technologies.",
  },
  {
    question: "How can you help us with SEO?",
    answer:
      "We can help you with on-page optimization, off-page optimization, local SEO, keyword research, content optimization, and technical SEO to improve your website's search engine ranking and visibility.",
  },
  {
    question: "What digital marketing services do you offer?",
    answer:
      "We offer a complete range of digital marketing services, including social media marketing, email marketing, PPC advertising, content marketing, and online reputation management.",
  },
  {
    question: "Can you help us with video editing and production?",
    answer:
      "Yes, we have a team of skilled video editors and producers who can help you with video editing, animation, motion graphics, and post-production services.",
  },
  {
    question: "Do you provide content writing services?",
    answer:
      "Yes, we provide high-quality content writing services, including website content, blog posts, articles, social media posts, and product descriptions.",
  },
  {
    question: "How long does it take to develop a website or a mobile app?",
    answer:
      "The development time depends on the complexity and features of the project. We can provide you with an estimated timeline and deliver the project within the deadline.",
  },
  {
    question: "How much does it cost to develop a website or a mobile app?",
    answer:
      "The cost of development depends on the project's scope, features, and requirements. We can provide you with a detailed proposal with a breakdown of costs and payment milestones.",
  },
  {
    question: "Do you provide post-development support and maintenance?",
    answer:
      "Yes, we provide post-development support and maintenance services, including bug fixing, security updates, performance optimization, and feature enhancements.",
  },
  {
    question: "How do you ensure the security of our website or app?",
    answer:
      "We follow industry-standard security practices and use the latest security tools and technologies to ensure the security of your website or app.",
  },
  {
    question: "Can you integrate third-party services or APIs into our website or app?",
    answer:
      "Yes, we have experience in integrating third-party services and APIs into websites and mobile apps, including payment gateways, social media platforms, and other web services.",
  },
  {
    question: "Do you provide hosting and domain registration services?",
    answer:
      "Yes, we can help you with hosting and domain registration services for your website or app.",
  },
  {
    question: "How do you ensure the quality of your work?",
    answer:
      "We have a team of skilled developers, designers, and digital marketing experts who follow a rigorous quality assurance process to ensure the quality of our work.",
  },
  {
    question: "Do you offer any guarantees or warranties?",
    answer:
      "Yes, we offer a warranty period for our work and provide guarantees on the functionality and performance of the software, website, or app we develop.",
  },
];

const popularTopics = ["Website", "Mobile app", "Support", "SEO"];

const FAQs = () => {
  const [query, setQuery] = useState("");

  const filteredFaqs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return faqItems;

    return faqItems.filter(({ question, answer }) =>
      `${question} ${answer}`.toLowerCase().includes(normalizedQuery)
    );
  }, [query]);

  const scrollToAnswers = () => {
    document.getElementById("faq-list")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleTopicClick = (topic) => {
    setQuery(topic);
    window.setTimeout(scrollToAnswers, 0);
  };

  return (
    <div className="overflow-x-hidden bg-slate-50">

      <section className="relative isolate min-h-[680px] overflow-hidden bg-[#070a10] px-5 pb-16 pt-28 text-white md:px-8 md:pt-32 lg:flex lg:min-h-[720px] lg:items-center lg:px-12 lg:pb-14 lg:pt-28">
        <div className="absolute inset-0 -z-30 bg-[radial-gradient(circle_at_10%_20%,rgba(100,60,144,0.46),transparent_28%),radial-gradient(circle_at_90%_12%,rgba(255,219,22,0.15),transparent_25%),radial-gradient(circle_at_64%_90%,rgba(35,155,230,0.17),transparent_30%)]" />
        <div className="absolute inset-0 -z-20 opacity-[0.065] [background-image:linear-gradient(rgba(255,255,255,0.45)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.45)_1px,transparent_1px)] [background-size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_96%)]" />
        <div className="absolute -left-28 top-36 -z-10 h-72 w-72 rounded-full border border-white/10" />
        <div className="absolute -left-12 top-52 -z-10 h-36 w-36 rounded-full border border-[#FFDB16]/20" />
        <div className="absolute right-[7%] top-32 -z-10 h-24 w-24 rotate-12 rounded-[28px] border border-white/10 bg-white/[0.025]" />

        <div className="mx-auto grid w-full max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 xl:gap-24">
          <div>
            <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 backdrop-blur-xl">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#FFDB16] text-slate-950">
                <FiHelpCircle className="h-4 w-4" />
              </span>
              <span className="text-sm font-bold tracking-wide text-slate-200">
                Help center
              </span>
            </div>

            <h1 className="max-w-3xl font-heading text-[2.8rem] font-extrabold leading-[1.03] tracking-[-0.045em] text-white sm:text-6xl lg:text-[4rem] xl:text-[4.5rem]">
              Questions deserve
              <span className="relative mt-1 block w-fit text-[#FFDB16]">
                clear answers.
                <span className="absolute -bottom-2 left-0 h-1 w-24 rounded-full bg-[#FFDB16] sm:w-32" />
              </span>
            </h1>

            <p className="mt-8 max-w-xl text-lg leading-8 text-slate-300 md:text-xl lg:text-lg xl:text-xl">
              Explore straightforward answers about our services, timelines,
              pricing, process, and ongoing support.
            </p>

            <form
              className="mt-8 max-w-2xl"
              onSubmit={(event) => {
                event.preventDefault();
                scrollToAnswers();
              }}
            >
              <label htmlFor="faq-search" className="sr-only">
                Search frequently asked questions
              </label>
              <div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white p-2 pl-4 shadow-2xl shadow-black/25">
                <FiSearch className="h-5 w-5 shrink-0 text-slate-400" />
                <input
                  id="faq-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search for services, pricing, timelines…"
                  className="min-w-0 flex-1 bg-transparent py-3 text-base text-slate-950 outline-none placeholder:text-slate-400"
                />
                <button
                  type="submit"
                  className="hidden min-h-[48px] items-center gap-2 rounded-xl bg-slate-950 px-5 font-bold text-white transition hover:bg-[#643c90] sm:flex"
                >
                  Find answers
                  <FiArrowDown className="h-4 w-4" />
                </button>
              </div>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <span className="mr-1 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                Popular
              </span>
              {popularTopics.map((topic) => (
                <button
                  key={topic}
                  type="button"
                  onClick={() => handleTopicClick(topic)}
                  className="rounded-full border border-white/10 bg-white/[0.055] px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-[#FFDB16]/50 hover:bg-white/[0.1] hover:text-[#FFDB16]"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[520px] lg:mx-0 lg:ml-auto">
            <div className="absolute -inset-5 -z-10 rounded-[40px] bg-gradient-to-br from-[#FFDB16]/20 via-transparent to-[#643c90]/40 blur-2xl" />
            <div className="rounded-[30px] border border-white/12 bg-white/[0.07] p-5 shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-7">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFDB16] text-slate-950">
                    <FiMessageCircle className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-heading text-2xl font-extrabold text-white">
                      {faqItems.length} answers
                    </p>
                    <p className="mt-0.5 text-sm text-slate-400">Ready when you need them</p>
                  </div>
                </div>
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.75)]" />
              </div>

              <div className="mt-5 space-y-3">
                {[
                  "How long will my project take?",
                  "Can you support us after launch?",
                  "What will our project cost?",
                ].map((question, index) => (
                  <div
                    key={question}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-slate-950/35 p-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.08] text-sm font-extrabold text-[#FFDB16]">
                      0{index + 1}
                    </span>
                    <p className="font-semibold text-slate-200">{question}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex items-center justify-between gap-4 rounded-2xl bg-[#643c90] p-4 sm:p-5">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-purple-200">
                    Still need help?
                  </p>
                  <p className="mt-1 font-bold text-white">Talk directly with our team.</p>
                </div>
                <Link
                  to="/contactus"
                  aria-label="Contact our team"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FFDB16] text-slate-950 transition hover:-translate-y-0.5 hover:bg-white"
                >
                  <FiArrowUpRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main id="faq-list" className="scroll-mt-24 px-5 py-16 md:px-8 md:py-20 lg:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-extrabold uppercase tracking-[0.14em] text-[#643c90]">
                Knowledge base
              </p>
              <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-950 md:text-4xl">
                Browse all questions
              </h2>
            </div>
            <p className="text-sm font-semibold text-slate-500">
              Showing {filteredFaqs.length} of {faqItems.length} answers
            </p>
          </div>

          {filteredFaqs.length > 0 ? (
            <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-xl shadow-slate-200/40">
              {filteredFaqs.map(({ question, answer }) => (
                <Accordion key={question} title={question}>
                  <p>{answer}</p>
                </Accordion>
              ))}
            </div>
          ) : (
            <div className="rounded-[26px] border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
              <FiSearch className="mx-auto h-8 w-8 text-slate-400" />
              <h3 className="mt-4 font-heading text-xl font-extrabold text-slate-900">
                No matching answers found
              </h3>
              <p className="mt-2 text-slate-500">Try a broader keyword or browse every question.</p>
              <button
                type="button"
                onClick={() => setQuery("")}
                className="mt-5 rounded-full bg-slate-950 px-5 py-2.5 font-bold text-white transition hover:bg-[#643c90]"
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQs;
